// @ts-nocheck
import { useState } from "react";
import { formatDualQty, packingLabel } from "@apv-erp/status";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Pencil,
  Trash2,
  Loader2,
  Layers,
  BarChart2,
  User,
  History,
  Package,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { EntityLink } from "@/components/shared/EntityLink";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { trpcClient } from "@/lib/trpc";
import {
  formatNumber,
  formatMoney,
  formatDate,
  getUploadUrl,
  renderDetailValue,
} from "@/lib/utils";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import ImageLightbox from "@/components/shared/ImageLightbox";
import { DetailField as F } from "@/components/shared/DetailField";

// ── Category Labels ────────────────────────────────────────────
const CATEGORY_LABELS: Record<string, string> = {
  PAPER: "Giấy",
  INK: "Mực",
  GLUE: "Keo",
  FILM: "Màng",
  CHEMICAL: "Hoá chất",
  OTHER: "Khác",
};
const CATEGORY_COLORS: Record<string, string> = {
  PAPER: "bg-amber-50 text-amber-700 border-amber-200",
  INK: "bg-violet-50 text-violet-700 border-violet-200",
  GLUE: "bg-cyan-50 text-cyan-700 border-cyan-200",
  FILM: "bg-blue-50 text-blue-700 border-blue-200",
  CHEMICAL: "bg-rose-50 text-rose-700 border-rose-200",
  OTHER: "bg-slate-50 text-slate-600 border-slate-200",
};


const MF = ({
  label,
  value,
  primary,
}: {
  label: string;
  value?: any;
  primary?: boolean;
}) => (
  <div className="flex gap-2 min-w-0">
    <span className="text-gray-500 text-xs shrink-0 w-24">{label}</span>
    <span
      className={`text-sm font-mono font-bold ${primary ? "text-indigo-600" : "text-slate-700"}`}
    >
      {value ? formatMoney(value) : "—"}
    </span>
  </div>
);

// ── Tab Keys ────────────────────────────────────────────────────
type TabKey = "audit" | "stats";

// ════════════════════════════════════════════════════════════════
export default function MaterialView({ id }: { id?: string } = {}) {
  const params = useParams({ strict: false }) as { materialId?: string };
  const materialId = id || params.materialId || "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: materialData, isLoading, error: detailError, refetch: refetchDetail } = useQuery({
    queryKey: ["material", materialId],
    queryFn: async () => trpcClient.materials.getById.query({ id: materialId }),
  });

  const material = materialData as any;

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await trpcClient.materials.delete.mutate({ id: materialId });
      await queryClient.invalidateQueries({ queryKey: ["materials"] });
      await queryClient.invalidateQueries({ queryKey: ["activity-log"] });
      await queryClient.invalidateQueries({ queryKey: ["materials-lookup"] });

      setIsDeleting(false);
      navigate({ to: "/materials" });
    } catch (err: any) {
      setIsDeleting(false);
      toast.error(err?.message || "Xoá thất bại");
    }
  };

  if (isLoading) {
    return <DetailLoadingSkeleton breadcrumbs={[{ label: "Vật tư", to: "/materials" }]} />;
  }

  if (detailError || !material) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Vật tư", to: "/materials" }]}
        entityLabel="Vật tư"
        backTo="/materials"
        codeOrId={materialId}
        error={detailError}
        onRetry={() => refetchDetail()}
      />
    );
  }

  const catLabel =
    CATEGORY_LABELS[material.category || ""] || material.category || "—";
  const catColor =
    CATEGORY_COLORS[material.category || ""] || CATEGORY_COLORS.OTHER;
  const isLowStock =
    material.currentStock != null &&
    material.minStock != null &&
    material.currentStock <= material.minStock;

  return (
    <StandardDetailLayout
      breadcrumbs={[{ label: "Vật tư", to: "/materials" }]}
      title={material.materialId}
      entityId={material?.id || materialId}
      entityType="material"
      timestamps={{
        created: material.createdAt,
        updated: material.updatedAt,
      }}
      status={
        <div className="flex gap-2">
          <Badge
            variant="outline"
            className={`text-xs px-2.5 py-0.5 ${catColor}`}
          >
            {catLabel}
          </Badge>
          {/* Bật / tắt Ngưng dùng: vật tư chưa có cột trạng thái (bấm không lưu) nên tạm ẩn (02/11/2026) */}
        </div>
      }
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate({
                to: "/materials/$materialId/edit",
                params: { materialId },
              })
            }
          >
            <Pencil size={14} className="mr-1.5" /> Chỉnh sửa
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-rose-600 border-rose-200 hover:bg-rose-50"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Xoá
          </Button>
        </div>
      }
      onBack={() => navigate({ to: "/materials" })}
    >
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Xác nhận xoá"
        description={`Bạn có chắc muốn xoá vật tư "${material.name}"?`}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />
      {/* ═══ FLAT INFO — 4 columns left & Image right ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        <div className="lg:col-span-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
            <F label="Mã VT" value={material.materialId} mono hl />
            <F label="Phân loại" value={catLabel} />
            <F
              label="Tên VT"
              value={material.name}
              hl
              className="sm:col-span-2 lg:col-span-3"
            />

            <F
              label="Loại vật tư"
              value={
                material.materialType?.id || material.materialType?.code ? (
                  <Link
                    to="/material-types"
                    search={{ view: material.materialType.id || material.materialType.code } as any}
                    className="text-indigo-600 hover:underline"
                    title="Xem loại vật tư"
                  >
                    {material.materialType?.label?.split(" - ")[0] || material.materialType?.code}
                  </Link>
                ) : (
                  material.subType
                )
              }
              className="sm:col-span-2"
            />
            <F label="Đơn vị tính" value={material.unit} />
            {/* ĐVT phụ + quy đổi (04/11/2026) */}
            {(material as any).secondaryUnit && (
              <F
                label="Quy đổi"
                value={`${packingLabel(material as any)}${(material as any).secondaryFactorAuto ? " (tự tính)" : ""}`}
              />
            )}
            {(material as any).packingNote && <F label="Đóng gói" value={(material as any).packingNote} />}

            <F
              label="Nhà cung cấp"
              value={
                material.vendorId ? (
                  <EntityLink
                    entityType="vendor"
                    id={material.vendorId}
                    code={material.vendor?.code}
                    label={
                      material.vendor?.fullName ||
                      material.vendor?.name ||
                      material.vendor?.shortName
                    }
                    search={{ returnUrl: `/materials/${materialId}` }}
                  />
                ) : (
                  material.vendor?.fullName ||
                  material.vendor?.name ||
                  material.vendor?.shortName ||
                  "—"
                )
              }
              className="sm:col-span-2"
            />
            <F
              label="Đơn giá"
              value={
                material.unitPrice
                  ? formatMoney(material.unitPrice) + " đ"
                  : "—"
              }
              hl
            />

            {/* Tồn kho */}
            <F
              label="Tồn kho"
              value={
                material.currentStock != null
                  ? formatDualQty(Number(material.currentStock), material as any)
                  : "—"
              }
              mono
              hl={isLowStock}
              className={isLowStock ? "text-rose-600" : "text-indigo-600"}
            />
            <F
              label="Tồn tối thiểu"
              value={formatNumber(material.minStock)}
              mono
            />
            <F
              label="Tồn ban đầu"
              value={formatNumber(material.initialStock)}
              mono
            />

            {/* Thông số kỹ thuật */}
            {(material as any).thicknessMicron != null && (
              <F label="Độ dày" value={`${Number((material as any).thicknessMicron)} micron`} />
            )}
            {material.gsm != null && (
              <F label="Định lượng" value={material.gsm + " g/m²"} />
            )}
            {((material.length != null && material.length !== "") ||
              (material.width != null && material.width !== "")) && (
              <F
                label="Quy cách"
                value={`${material.length || ""}${material.length && material.width ? "x" : ""}${material.width || ""}mm`}
              />
            )}

            {material.specs && (
              <F
                label="Thông số KT"
                value={material.specs}
                className="sm:col-span-2 lg:col-span-3"
              />
            )}
            {material.note && (
              <F
                label="Ghi chú"
                value={material.note}
                className="sm:col-span-2 lg:col-span-3"
              />
            )}
          </div>
        </div>

        {/* RIGHT: Image */}
        <div className="lg:col-span-1">
          {material.image ? (
            <ImageLightbox src={getUploadUrl(material.image)} alt={material.name || material.code || "Material Image"}>
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white p-1 shadow-sm cursor-zoom-in hover:border-indigo-300 hover:shadow-md transition-all group">
                <img
                  src={getUploadUrl(material.image)}
                  alt="Material Image"
                  className="object-cover w-full h-auto max-h-[300px] rounded-md transition-transform duration-200 group-hover:scale-[1.02]"
                />
              </div>
            </ImageLightbox>
          ) : (
            <div className="border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-center p-8 shadow-sm flex-col gap-2 min-h-[200px]">
              <div className="h-10 w-10 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center mb-1 bg-white">
                <Package className="h-5 w-5 text-slate-300" />
              </div>
              <span className="text-xs text-slate-400 font-medium">
                Không có ảnh
              </span>
            </div>
          )}
        </div>
      </div>
    </StandardDetailLayout>
  );
}
